//
// # Manhattan Router
//
// Routes consist of horizontal and vertical segments only, with minimized
// bends. Between two points that share no axis there are exactly two
// minimum-bend orthogonal routes — horizontal-first and vertical-first — so
// routing is a choice between two candidates, not a search.
//
// This previously ran A* over an implicit lattice. With no obstacles and a
// uniform cost surface the optimum is always a straight line or a single L,
// so the search expanded nodes proportional to the enclosing rectangle's area
// (re-sorting its open set every iteration) to rediscover a result available
// in constant time — and on long drags exhausted its iteration cap and fell
// back to the L anyway.
//
// The one thing a search bought was the *possibility* of obstacle awareness,
// which it never used. That is provided directly instead: when obstacles are
// supplied, the two candidates are scored by how many they cross and the
// cleaner one wins. Ties keep the long-standing heuristic — travel along the
// larger separation first.
//
// Note this router does NOT snap to a grid. It used to, against a hardcoded
// size of 10, while the editor's snap is a user setting; when the two
// disagreed a route silently stopped landing on the pin it was asked to
// reach. Callers work in already-snapped coordinates and are responsible for
// grid discipline.
//

import { Point, model } from "GenAlphaModel";

// Direction of travel
export type Direction = "up" | "down" | "left" | "right" | "none";

// Which leg a route travels first.
export type FirstLeg = "horizontal" | "vertical";

// Router configuration
export interface RouterConfig {
  // Grid spacing. Retained for callers that construct a router with a grid
  // size; `route` no longer snaps to it. See the note above.
  gridSize: number;
  // Cost for each grid step
  stepCost: number;
  // Additional cost for changing direction (to minimize bends)
  bendCost: number;
}

// Default configuration
export const DEFAULT_ROUTER_CONFIG: RouterConfig = {
  gridSize: 10,
  stepCost: 1,
  bendCost: 5, // Penalize bends to get cleaner routes
};

// Options for a single route.
export interface RouteOptions {
  // Regions the route should avoid crossing, typically instance bounding
  // boxes. Only used to choose between the two candidate L-shapes: if both
  // cross the same number of obstacles the choice falls back to the
  // separation heuristic. A route is never abandoned for lack of a clean
  // option — some pin pairs have none.
  //
  // The caller is responsible for excluding the boxes belonging to whatever
  // sits at `start` and `end`; a route terminating on a pin necessarily
  // reaches that pin's own instance.
  obstacles?: model.Bbox[];
  // Force which leg travels first, overriding both obstacle scoring and the
  // separation heuristic.
  firstLeg?: FirstLeg;
}

// Result of routing
export interface RouteResult {
  success: boolean;
  // Path as a list of points (start to end)
  path: Point[];
  // Path as wire segments (pairs of consecutive points)
  segments: Array<[Point, Point]>;
  // Number of bends in the route
  bendCount: number;
  // Retained for API compatibility; always 0 now that routing is direct.
  iterations: number;
}

//
// # Pure Functions
//

// Convert path to wire segments
export function pathToSegments(path: Point[]): Array<[Point, Point]> {
  const segments: Array<[Point, Point]> = [];

  for (let i = 0; i < path.length - 1; i++) {
    segments.push([path[i], path[i + 1]]);
  }

  return segments;
}

// Count bends in a path
export function countBends(path: Point[]): number {
  if (path.length <= 2) return 0;

  let bends = 0;
  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const next = path[i + 1];

    // Check if direction changes at this point
    const dir1Horizontal = prev.y === curr.y;
    const dir2Horizontal = curr.y === next.y;

    if (dir1Horizontal !== dir2Horizontal) {
      bends++;
    }
  }

  return bends;
}

// Does the axis-aligned segment `a`-`b` cross the interior of `box`?
//
// Interior, not closure: a route running exactly along a box's edge, or
// terminating on it, is not crossing it. Pins sit on their symbol's boundary,
// so a closed test would report every route as blocked at its own endpoints.
export function segmentCrossesBbox(
  a: Point,
  b: Point,
  box: model.Bbox
): boolean {
  const xMin = Math.min(box.min.x, box.max.x);
  const xMax = Math.max(box.min.x, box.max.x);
  const yMin = Math.min(box.min.y, box.max.y);
  const yMax = Math.max(box.min.y, box.max.y);

  if (a.y === b.y) {
    if (a.y <= yMin || a.y >= yMax) return false;
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    return Math.min(hi, xMax) - Math.max(lo, xMin) > 0;
  }
  if (a.x === b.x) {
    if (a.x <= xMin || a.x >= xMax) return false;
    const lo = Math.min(a.y, b.y);
    const hi = Math.max(a.y, b.y);
    return Math.min(hi, yMax) - Math.max(lo, yMin) > 0;
  }
  return false; // diagonal: not a route this module produces
}

// How many of `obstacles` a candidate path crosses.
function obstacleCount(path: Point[], obstacles: model.Bbox[]): number {
  let count = 0;
  for (const box of obstacles) {
    for (const [a, b] of pathToSegments(path)) {
      if (segmentCrossesBbox(a, b, box)) {
        count++;
        break; // one crossing per obstacle is enough to rank it
      }
    }
  }
  return count;
}

// The bend point of an L travelling `firstLeg` first.
function bendPoint(start: Point, end: Point, firstLeg: FirstLeg): Point {
  return firstLeg === "horizontal"
    ? { x: end.x, y: start.y }
    : { x: start.x, y: end.y };
}

// Build the result for a path.
function result(path: Point[]): RouteResult {
  return {
    success: true,
    path,
    segments: pathToSegments(path),
    bendCount: countBends(path),
    iterations: 0,
  };
}

//
// # Main Router Class
//

export class ManhattanRouter {
  private readonly config: RouterConfig;

  constructor(config: Partial<RouterConfig> = {}) {
    this.config = { ...DEFAULT_ROUTER_CONFIG, ...config };
  }

  // Find a Manhattan route between two points.
  //
  // Degenerate cases are reported rather than papered over: coincident points
  // yield a single-point path with `bendCount` 0, which callers must handle —
  // there is no segment to draw between a point and itself.
  route(start: Point, end: Point, opts: RouteOptions = {}): RouteResult {
    if (Point.eq(start, end)) return result([start]);

    // Already aligned on one axis: a single straight segment, no choice to
    // make and nothing an obstacle could change.
    if (start.x === end.x || start.y === end.y) return result([start, end]);

    if (opts.firstLeg) {
      return result([start, bendPoint(start, end, opts.firstLeg), end]);
    }

    // Travel along the larger separation first. This is the long-standing
    // tie-break and the answer whenever obstacles do not distinguish the two.
    const preferred: FirstLeg =
      Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)
        ? "horizontal"
        : "vertical";
    const alternate: FirstLeg =
      preferred === "horizontal" ? "vertical" : "horizontal";

    const preferredPath = [start, bendPoint(start, end, preferred), end];
    const obstacles = opts.obstacles ?? [];
    if (obstacles.length === 0) return result(preferredPath);

    const preferredHits = obstacleCount(preferredPath, obstacles);
    if (preferredHits === 0) return result(preferredPath);

    // Only switch when the alternate is strictly cleaner, so the heuristic
    // still decides whenever both routes are equally obstructed.
    const alternatePath = [start, bendPoint(start, end, alternate), end];
    const alternateHits = obstacleCount(alternatePath, obstacles);
    return result(
      alternateHits < preferredHits ? alternatePath : preferredPath
    );
  }

  // The configured grid size, for callers that snap before routing.
  get gridSize(): number {
    return this.config.gridSize;
  }
}
